import { Schema } from '../../../../adapters/schema';
import { makePathModel, asTypeProxy } from '../../../../utils/nestedPathModel';
import { OCCUPATION_TYPES } from './formModel.types';
import constants from './constants';
import { getTranslation } from '../../../../utils/translations';
import translations from './translations';
import postalAddressSubformTranslations from './postalAddressSubform.translations';
import { makePostalAddressSubformModel } from '../../../../subforms/PostalAddressSubForm/formModel';
var t = getTranslation(translations);
var translationsOverride = getTranslation(postalAddressSubformTranslations);
var postalAddressSubformModel = makePostalAddressSubformModel({
    translationsOverride: translationsOverride,
});
var initialValues = {
    applicantsAmount: 1,
    isSameHousehold: false,
    applicant: {
        occupationType: OCCUPATION_TYPES.EMPLOYEE,
        isOnProbationPeriod: false,
        isLimitedOccupation: false,
    },
    secondApplicant: {
        occupationType: OCCUPATION_TYPES.EMPLOYEE,
    },
};
var fields = asTypeProxy();
// @todo: EXTRA UGLY!!! Possible solution: nested forms
var PATHS = {
    APPLICANTS_AMOUNT: fields.applicantsAmount,
    IS_SAME_HOUSEHOLD: fields.isSameHousehold,
    OCCUPATION_TYPE: fields.applicant.occupationType,
    INCOME: fields.applicant.income,
    NUMBER_OF_SALARIES_PER_YEAR: fields.applicant.numberOfSalariesPerYear,
    IS_ON_PROBATION_PERIOD: fields.applicant.isOnProbationPeriod,
    OCCUPATION_BEGIN: fields.applicant.occupationBegin,
    IS_LIMITED_OCCUPATION: fields.applicant.isLimitedOccupation,
    OCCUPATION_LIMIT_UNTIL: fields.applicant.occupationLimitedUntil,
    EMPLOYER_ADDRESS_COUNTRY: fields.applicant.employerAddressCountry,
    POSTAL_ADDRESS: fields.applicant.postalAddress,
    SECOND_APPLICANT_OCCUPATION_TYPE: fields.secondApplicant.occupationType,
    SECOND_APPLICANT_INCOME: fields.secondApplicant.income,
    SECOND_APPLICANT_POSTAL_ADDRESS: fields.secondApplicant.postalAddress,
};
var _a = makePathModel(PATHS), paths = _a[0], selectors = _a[1];
//@todo: [nested-forms] doubles of helpers for different applicants leads to necessity of separated
// nested forms with own validation rules
var helpers = {
    shouldShowSecondApplicant: function (values) {
        return selectors.APPLICANTS_AMOUNT(values) === 2;
    },
    shouldShowSecondApplicantPostalAddress: function (values) {
        return selectors.IS_SAME_HOUSEHOLD(values) === false;
    },
    isSalariedEmployee: function (values) {
        return constants.occupationTypesForSalariedEmployee.includes(selectors.OCCUPATION_TYPE(values));
    },
    shouldShowNumberOfSalaries: function (values) {
        return helpers.isSalariedEmployee(values);
    },
    shouldShowIsOnProbationPeriod: function (values) {
        return helpers.isSalariedEmployee(values);
    },
    shouldShowOccupationBegin: function (values) {
        return !!selectors.IS_ON_PROBATION_PERIOD(values);
    },
    shouldShowIsLimitedOccupation: function (values) {
        return helpers.isSalariedEmployee(values);
    },
    shouldShowOccupationLimitedUntil: function (values) {
        return !!selectors.IS_LIMITED_OCCUPATION(values);
    },
    deriveIsAnnualNetCaseFromOccupationType: function (value) {
        return constants.occupationTypesWithAnnualIncome.includes(value);
    },
    deriveEmployerAddressCountryVisibilityFromOccupationType: function (value) {
        return constants.occupationTypesRelatedToCountry.includes(value);
    },
    shouldShowEmployerAddressCountry: function (values) {
        return helpers.deriveEmployerAddressCountryVisibilityFromOccupationType(selectors.OCCUPATION_TYPE(values));
    },
    deriveLabelForIncomeFieldFromOccupationType: function (value) {
        var isAnnualNetCase = helpers.deriveIsAnnualNetCaseFromOccupationType(value);
        return isAnnualNetCase
            ? t.incomeLabel_annualNetCase
            : t.incomeLabel_monthlyNetCase;
    },
    getLabelForIncomeField: function (values) {
        return helpers.deriveLabelForIncomeFieldFromOccupationType(selectors.OCCUPATION_TYPE(values));
    },
    getLabelForSecondApplicantOccupationField: function (values) {
        return helpers.deriveLabelForIncomeFieldFromOccupationType(selectors.SECOND_APPLICANT_OCCUPATION_TYPE(values));
    },
    deriveIncomeFieldDescriptionFromOccupationType: function (value) {
        return helpers.deriveIsAnnualNetCaseFromOccupationType(value)
            ? t.incomeDescription_annualNetCase
            : t.incomeDescription_common;
    },
    getDescriptionForIncomeField: function (values) {
        return helpers.deriveIncomeFieldDescriptionFromOccupationType(selectors.OCCUPATION_TYPE(values));
    },
    getDescriptionForSecondApplicantOccupationField: function (values) {
        return helpers.deriveIncomeFieldDescriptionFromOccupationType(selectors.SECOND_APPLICANT_OCCUPATION_TYPE(values));
    },
};
function makeSchemaForDateField(visibilitySwitcherFieldName, label) {
    return Schema.date().when(['occupationType', visibilitySwitcherFieldName], {
        is: function (occupationType, visibilitySwitcherValue) {
            var isSalaryType = constants.occupationTypesForSalariedEmployee.includes(occupationType);
            return isSalaryType && visibilitySwitcherValue;
        },
        then: Schema.date().label(label).required(),
    });
}
function makeSchemaForCurrencyField() {
    return Schema.number()
        .required()
        .min(1)
        .when('occupationType', 
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function (value, schema) {
        var label = helpers.deriveLabelForIncomeFieldFromOccupationType(value);
        return schema.label(label);
    });
}
var applicantSchema = Schema.object().shape({
    income: makeSchemaForCurrencyField(),
    occupationBegin: makeSchemaForDateField('isOnProbationPeriod', t.occupationBeginLabel),
    occupationLimitedUntil: makeSchemaForDateField('isLimitedOccupation', t.occupationLimitedUntilLabel),
    employerAddressCountry: Schema.mixed().when('occupationType', {
        is: function (value) {
            return helpers.deriveEmployerAddressCountryVisibilityFromOccupationType(value);
        },
        then: Schema.mixed()
            .oneOf(constants.employerAddressCountryOptions.map(function (_a) {
            var value = _a.value;
            return value;
        }))
            .label(t.employerAddressCountryLabel)
            .defined(),
    }),
    postalAddress: postalAddressSubformModel.validationSchema,
});
var secondApplicantSchema = Schema.object().shape({
    income: makeSchemaForCurrencyField(),
    postalAddress: Schema.object().when(['$values'], {
        is: function (values) {
            return helpers.shouldShowSecondApplicantPostalAddress(values);
        },
        then: postalAddressSubformModel.validationSchema,
    }),
});
var validationSchema = Schema.object().shape({
    applicantsAmount: Schema.number().required(),
    applicant: applicantSchema,
    secondApplicant: Schema.object().when('applicantsAmount', {
        is: 2,
        then: secondApplicantSchema,
    }),
});
export var applicantOccupationPageModel = {
    initialValues: initialValues,
    paths: paths,
    selectors: selectors,
    helpers: helpers,
    validationSchema: validationSchema,
};
